import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, CheckCircle2, XCircle, LogIn } from "lucide-react";
import api from "@/lib/api";
import { setSession } from "@/lib/auth";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Loads the Turnstile script once (safe to call from multiple mounts —
 * checks for an existing tag first) and resolves once window.turnstile
 * is actually ready to render.
 */
function loadTurnstile() {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve(true);
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * TeamLogin.jsx
 * ------------------------------------------------------------------
 * Route: /team/login          -> email entry form (request a link)
 * Route: /team/login?token=.. -> verifies the link, logs the member in
 * ------------------------------------------------------------------
 */

export default function TeamLogin() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return token ? <VerifyLink token={token} /> : <RequestLink />;
}

// -------------------- Step 1: request a link -------------------------------
function RequestLink() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((ok) => {
      if (cancelled || !ok || !widgetRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
      setCaptchaReady(true);
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErr("Enter a valid email address.");
      return;
    }
    if (!captchaToken) {
      setErr("Please complete the verification check.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await api.post("/team/login/request", {
        email: email.trim().toLowerCase(),
        turnstile_token: captchaToken,
      });
      setSent(true);
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === "captcha_failed") {
        // Real error, unlike a "wrong email" case — the backend always
        // says 200 for that on purpose. A failed captcha means the token
        // was rejected or reused, so reset the widget and let them retry.
        setErr("Verification failed — please try the check again.");
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
        setCaptchaToken("");
      } else {
        // Any other failure (network hiccup, etc.) — backend always
        // responds 200 for a normal request, so this branch is rare.
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const Icon = sent ? CheckCircle2 : LogIn;
  const iconBg = sent ? "bg-mint" : "bg-lilac";

  return (
    <section className="grid min-h-screen place-items-center bg-cream px-6">
      <div className="w-full max-w-md rounded-3xl border border-ink/10 bg-white p-8 shadow-sm">
        <div className={`grid h-14 w-14 place-items-center rounded-2xl ${iconBg} text-ink`}>
          <Icon className="h-6 w-6" />
        </div>

        {!sent ? (
          <>
            <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">Team member login</h1>
            <p className="mt-2 text-sm text-ink/60">
              Enter the email your invite was sent to. We'll send you a one-time login link.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                <input
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbrand.com"
                  className="w-full rounded-2xl border border-ink/15 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-hotpink"
                  data-testid="team-login-email"
                />
              </div>
              {err && <p className="text-xs font-semibold text-hotpink">{err}</p>}
              <div ref={widgetRef} data-testid="team-login-captcha" />
              <button
                type="submit"
                disabled={busy || !captchaReady || !captchaToken}
                data-testid="team-login-submit"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-70"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send login link
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">Check your email</h1>
            <p className="mt-2 text-sm text-ink/60">
              If <span className="font-semibold text-ink">{email}</span> is on a team, a login link
              is on its way. It's valid for 1 hour and works once.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

// -------------------- Step 2: verify the link -------------------------------
const VERIFY_ICON_BY_STATUS = {
  success: { Icon: CheckCircle2, bg: "bg-mint" },
  error: { Icon: XCircle, bg: "bg-peach" },
};

function VerifyLink({ token }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | success | error

  useEffect(() => {
    api
      .post("/team/login/verify", { token })
      .then(({ data }) => {
        setSession(data.token, data.member?.name || data.member?.email || "");
        setStatus("success");
        setTimeout(() => navigate("/dashboard"), 1000);
      })
      .catch(() => setStatus("error"));
  }, [token, navigate]);

  return (
    <section className="grid min-h-screen place-items-center bg-cream px-6">
      <div className="w-full max-w-md rounded-3xl border border-ink/10 bg-white p-8 text-center shadow-sm">
        {status === "loading" && (
          <>
            <h1 className="text-2xl font-black tracking-tight text-ink">Logging you in…</h1>
            <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-ink/40" />
          </>
        )}

        {status !== "loading" && (
          <>
            {(() => {
              const { Icon, bg } = VERIFY_ICON_BY_STATUS[status];
              return (
                <div className={`mx-auto grid h-14 w-14 place-items-center rounded-2xl ${bg} text-ink`}>
                  <Icon className="h-7 w-7" />
                </div>
              );
            })()}

            {status === "success" && (
              <>
                <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">You're logged in</h1>
                <p className="mt-2 text-sm text-ink/60">Taking you to the dashboard…</p>
              </>
            )}

            {status === "error" && (
              <>
                <h1 className="mt-6 text-2xl font-black tracking-tight text-ink">Link expired or already used</h1>
                <p className="mt-2 text-sm text-ink/60">
                  Login links work once and expire after an hour. Request a new one below.
                </p>
                <Link
                  to="/team/login"
                  className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
                >
                  Get a new login link
                </Link>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
